import { init, parse } from 'es-module-lexer';
import type { ImportMap } from '@ms-cloudpack/import-map';
import type { PartialContext } from '@ms-cloudpack/api-server';
import fsPromises from 'fs/promises';

let initialized = false;

/**
 * Rewrites bare module imports in source code to use full URLs from the import map.
 * This allows workers (including service workers) and their dependencies to function without es-module-shims.
 *
 * @param source - The original source code
 * @param importMap - The import map to resolve bare imports against
 * @param bundleServerUrl - The bundle server base URL for resolving relative paths
 * @returns The rewritten source code with all bare imports replaced with full URLs
 */
export async function rewriteImports(source: string, importMap: ImportMap, bundleServerUrl?: string): Promise<string> {
  if (!initialized) {
    // Await init once for the Web Assembly boot to call parse synchronously.
    await init;
    initialized = true;
  }

  // Parse the source to find all imports
  const [imports] = parse(source);

  // If no imports, return source as-is
  if (imports.length === 0) {
    return source;
  }

  // Build a list of replacements to apply (in reverse order to maintain correct positions)
  const replacements: Array<{ start: number; end: number; replacement: string }> = [];

  for (const imp of imports) {
    // es-module-lexer behavior differs by import type:
    // - Static imports (t === 1): s/e positions EXCLUDE quotes
    // - Dynamic imports (t === 2): s/e positions INCLUDE quotes
    const isDynamicImport = imp.t === 2;
    const importSpecifierRaw = source.substring(imp.s, imp.e);

    const importSpecifier = isDynamicImport
      ? importSpecifierRaw.slice(1, -1) // Strip quotes for dynamic imports
      : importSpecifierRaw; // No stripping for static imports

    // For relative imports, add the rewriteimports query param so nested imports also get rewritten
    if (importSpecifier.startsWith('./') || importSpecifier.startsWith('../')) {
      const separator = importSpecifier.includes('?') ? '&' : '?';
      const rewrittenPath = `${importSpecifier}${separator}rewriteimports=true`;
      replacements.push({
        start: imp.s,
        end: imp.e,
        replacement: isDynamicImport
          ? `${importSpecifierRaw[0]}${rewrittenPath}${importSpecifierRaw[0]}`
          : rewrittenPath,
      });
      continue;
    }

    // Skip absolute URLs that already have the query param or are external
    if (importSpecifier.startsWith('http://') || importSpecifier.startsWith('https://')) {
      continue;
    }

    // For absolute paths (starting with /), add the query param
    if (importSpecifier.startsWith('/')) {
      const separator = importSpecifier.includes('?') ? '&' : '?';
      const rewrittenPath = `${importSpecifier}${separator}rewriteimports=true`;
      replacements.push({
        start: imp.s,
        end: imp.e,
        replacement: isDynamicImport
          ? `${importSpecifierRaw[0]}${rewrittenPath}${importSpecifierRaw[0]}`
          : rewrittenPath,
      });
      continue;
    }

    // This is a bare import - try to resolve it from the import map
    const resolvedUrl = resolveFromImportMap(importSpecifier, importMap, bundleServerUrl);

    if (resolvedUrl) {
      // Append rewriteimports query param so nested imports also get rewritten
      const separator = resolvedUrl.includes('?') ? '&' : '?';
      const urlWithRewrite = `${resolvedUrl}${separator}rewriteimports=true`;

      replacements.push({
        start: imp.s,
        end: imp.e,
        replacement: isDynamicImport
          ? `${importSpecifierRaw[0]}${urlWithRewrite}${importSpecifierRaw[0]}`
          : urlWithRewrite,
      });
    } else {
      // If we can't resolve it, log a warning but don't fail
      console.warn(`Could not resolve bare import "${importSpecifier}" in worker. It will remain as-is.`);
    }
  }

  // Apply replacements in reverse order to maintain correct positions
  let rewrittenSource = source;
  for (const replacement of replacements.reverse()) {
    rewrittenSource =
      rewrittenSource.substring(0, replacement.start) +
      replacement.replacement +
      rewrittenSource.substring(replacement.end);
  }

  return rewrittenSource;
}

/**
 * Resolves a bare import specifier using the import map.
 *
 * @param specifier - The bare import specifier (e.g., "uuid" or "uuid/v4")
 * @param importMap - The import map to resolve against
 * @param bundleServerUrl - Optional bundle server URL to make paths absolute
 * @returns The resolved URL, or undefined if not found
 */
function resolveFromImportMap(specifier: string, importMap: ImportMap, bundleServerUrl?: string): string | undefined {
  const { imports } = importMap;

  if (!imports) {
    return undefined;
  }

  // First try exact match
  if (imports[specifier]) {
    return makeAbsoluteUrl(imports[specifier], bundleServerUrl);
  }

  // Then try prefix matching (for package subpaths)
  // Import maps support trailing slash patterns like "uuid/" -> "/.../uuid/"
  const matchingKeys = Object.keys(imports)
    .filter((key) => key.endsWith('/') && specifier.startsWith(key))
    .sort((a, b) => b.length - a.length); // Sort by length descending for most specific match

  for (const key of matchingKeys) {
    const replacement = imports[key];
    const subpath = specifier.substring(key.length);
    const resolvedUrl = replacement.endsWith('/') ? replacement + subpath : replacement;
    return makeAbsoluteUrl(resolvedUrl, bundleServerUrl);
  }

  return undefined;
}

/**
 * Makes a URL absolute if it's relative and a base URL is provided.
 */
function makeAbsoluteUrl(url: string, baseUrl?: string): string {
  // Already absolute
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Make absolute using base URL if provided
  if (baseUrl) {
    // Remove trailing slash from base URL
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    // Ensure URL starts with /
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${cleanBase}${cleanUrl}`;
  }

  return url;
}

/**
 * Reads a file and rewrites bare imports to use import map URLs.
 * This is used for workers and their nested imports.
 *
 * @param filePath - The file path to read and rewrite
 * @param context - The session context with importMap and config
 * @returns The rewritten source code
 */
export async function rewriteImportsFromFile(
  filePath: string,
  context: PartialContext<'session', 'importMap' | 'urls' | 'config'>,
): Promise<string> {
  const { session } = context;

  const source = await fsPromises.readFile(filePath, 'utf8');

  if (!session.importMap) {
    throw new Error('Import map is not available');
  }

  const bundleServerUrl = session.config.features?.relativeImportMapPaths ? undefined : session.urls.bundleServer;
  return rewriteImports(source, session.importMap, bundleServerUrl);
}
